import { useEffect, useRef } from "react";

type MaybeElement = HTMLElement | SVGElement | null | undefined;
type MaybeRef<T extends MaybeElement = HTMLElement | SVGElement> =
  | React.RefObject<T | null>
  | T
  | null
  | undefined;

export type ClickOutsideEvent = MouseEvent | PointerEvent | TouchEvent | FocusEvent | Event;

export type UseClickOutsideOptions = {
  /**
   * Enable/disable the listener (default: true)
   */
  enabled?: boolean;
  /**
   * Additional elements treated as "inside" (e.g., portaled content)
   */
  include?: MaybeRef[];
  /**
   * Elements that should prevent triggering when interacted with
   * (treated as "inside" for check purposes)
   */
  ignore?: MaybeRef[];
  /**
   * Custom predicate to skip triggering
   */
  ignorePredicate?: (event: ClickOutsideEvent) => boolean;
  /**
   * Event types used to detect outside interactions.
   * Defaults to pointerdown and focusin to cover mouse/touch and keyboard.
   */
  eventTypes?: Array<"pointerdown" | "mousedown" | "touchstart" | "click" | "focusin">;
  /**
   * Whether to allow right/middle clicks to trigger (default: false = only primary/left)
   */
  allowRightClick?: boolean;
  /**
   * Listen in capture phase to catch events before stopPropagation (default: true)
   */
  capture?: boolean;
  /**
   * Attempt to detect clicks into iframes via window blur (default: true)
   */
  detectIframe?: boolean;
};

function isNode(value: unknown): value is Node {
  return !!value && typeof (value as { nodeType?: unknown }).nodeType === "number";
}

function resolveToElement(ref: MaybeRef): MaybeElement {
  if (!ref) return null;
  if (typeof ref === "object" && ref !== null && "current" in (ref as object)) {
    return (ref as React.RefObject<HTMLElement | SVGElement | null>).current ?? null;
  }
  return (ref as HTMLElement | SVGElement) ?? null;
}

function getComposedPath(event: Event): EventTarget[] {
  const anyEvent = event as Event & { composedPath?: () => EventTarget[] };
  if (typeof anyEvent.composedPath === "function") {
    return anyEvent.composedPath();
  }
  // Fallback for very old browsers
  const path: EventTarget[] = [];
  let target = event.target as Node | null;
  while (target) {
    path.push(target);
    target = (target as Node).parentNode;
  }
  path.push(window);
  return path;
}

function eventIsPrimary(event: ClickOutsideEvent, allowRightClick: boolean): boolean {
  // Only enforce button checks for mouse/pointer events
  if ("button" in event) {
    const e = event as MouseEvent | PointerEvent;
    // Left button is 0. If allowing right/middle, skip this check.
    if (!allowRightClick && e.button !== 0) return false;
  }
  if ("isPrimary" in event) {
    const pe = event as PointerEvent;
    // For pointer events, primary ensures we're not handling multi-touch secondary points
    if (!pe.isPrimary) return false;
  }
  return true;
}

function nodesContainTarget(nodes: MaybeElement[], target: Node, path: EventTarget[]): boolean {
  for (const n of nodes) {
    if (!n) continue;
    // Direct contains check
    if (n.contains(target)) return true;
    // Shadow DOM/portal-friendly: if the composed path includes the node
    if (path.includes(n)) return true;
  }
  return false;
}

/**
 * useClickOutside
 *
 * Triggers the callback when an interaction happens outside the provided element(s).
 * - Handles mouse/touch/pointer and keyboard focus (via focusin)
 * - Works with Shadow DOM and React portals (via composedPath)
 * - Supports multiple refs, include/ignore lists, and iframe focus detection
 */
export function useClickOutside(
  refs: MaybeRef | MaybeRef[],
  onOutside: (event: ClickOutsideEvent) => void,
  options: UseClickOutsideOptions = {},
): void {
  const {
    enabled = true,
    include = [],
    ignore = [],
    ignorePredicate,
    eventTypes = ["pointerdown", "focusin"],
    allowRightClick = false,
    capture = true,
    detectIframe = true,
  } = options;

  const latestCallback = useRef(onOutside);
  useEffect(() => {
    latestCallback.current = onOutside;
  }, [onOutside]);

  // Normalize refs to an array for resolution at event time
  const refsArray = Array.isArray(refs) ? refs : [refs];

  useEffect(() => {
    if (!enabled) return;

    const getInsideNodes = (): MaybeElement[] => {
      const primary = refsArray.map(resolveToElement);
      const inc = include.map(resolveToElement);
      const ign = ignore.map(resolveToElement);
      // Treat ignore nodes as inside to suppress triggers when interacting with them
      return [...primary, ...inc, ...ign];
    };

    const addOpts: AddEventListenerOptions = { capture, passive: true };
    const removeOpts: EventListenerOptions = { capture };

    const handler = (event: Event) => {
      if (!enabled) return;
      if (!event) return;

      if (ignorePredicate?.(event)) {
        return;
      }

      const path = getComposedPath(event);
      const target = path?.[0] || event.target;
      if (!isNode(target)) return;

      // Only trigger on primary pointer/mouse by default
      if (!eventIsPrimary(event as ClickOutsideEvent, allowRightClick)) {
        return;
      }

      const insideNodes = getInsideNodes().filter(Boolean) as Node[];
      if (insideNodes.length === 0) {
        // No targets registered yet; do nothing
        return;
      }

      // If the event happened inside any of the inside nodes, ignore
      if (nodesContainTarget(insideNodes as MaybeElement[], target, path)) {
        return;
      }

      latestCallback.current(event as ClickOutsideEvent);
    };

    // Attach requested event types
    for (const type of eventTypes) {
      document.addEventListener(type, handler, addOpts);
    }

    // Handle iframe clicks (focus moves into iframe causing window blur)
    const onWindowBlur = (event: FocusEvent) => {
      if (!detectIframe) return;
      // Delay to allow document.activeElement to settle
      setTimeout(() => {
        const active = document.activeElement;
        if (!active || active.tagName !== "IFRAME") return;
        const insideNodes = getInsideNodes().filter(Boolean) as Node[];
        // If the active iframe is not within our inside nodes, treat as outside
        if (!nodesContainTarget(insideNodes as MaybeElement[], active, [])) {
          latestCallback.current(event);
        }
      }, 0);
    };
    if (detectIframe) {
      window.addEventListener("blur", onWindowBlur, true);
    }

    return () => {
      for (const type of eventTypes) {
        document.removeEventListener(type, handler, removeOpts);
      }
      if (detectIframe) {
        window.removeEventListener("blur", onWindowBlur, true);
      }
    };
  }, [
    enabled,
    refsArray,
    include,
    ignore,
    ignorePredicate,
    eventTypes,
    allowRightClick,
    capture,
    detectIframe,
  ]);
}
